import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  FaBalanceScale,
  FaBox,
  FaCheckCircle,
  FaChevronLeft,
  FaChevronRight,
  FaHeart,
  FaList,
  FaMapMarkerAlt,
  FaQuestionCircle,
  FaSearch,
  FaStar,
  FaTh,
  FaTimes,
} from "react-icons/fa";
import { toast } from "react-toastify";
import B2BHubHomeHeader from "../../components/B2BHubHomeHeader";
import HubFloatingRail from "../../components/hub/HubFloatingRail";
import ContractManufacturersFiltersPanel from "../../components/marketplace/ContractManufacturersFiltersPanel";
import ContractManufacturerComparisonModal from "../../components/marketplace/ContractManufacturerComparisonModal";
import ExploreCmIndustryManufacturersBrowse from "../../components/marketplace/ExploreCmIndustryManufacturersBrowse";
import ExploreProductCategoriesBrowse from "../../components/marketplace/ExploreProductCategoriesBrowse";
import ExploreSubcategoryProductsBrowse from "../../components/marketplace/ExploreSubcategoryProductsBrowse";
import MarketplaceProductComparisonModal from "../../components/marketplace/MarketplaceProductComparisonModal";
import ProductMarketplaceFiltersPanel from "../../components/marketplace/ProductMarketplaceFiltersPanel";
import { API_URLS } from "../../constants/api-urls";
import {
  CM_INDUSTRY_BROWSE_OPTIONS,
  CM_INDUSTRY_IMAGE_BY_SLUG,
  industrySlugToLabel,
  industryTypeToSlug,
} from "../../constants/industryTypeMap";
import StorageService from "../../services/StorageService";
import MarketplaceAddToCartButton from "../../components/marketplace/MarketplaceAddToCartButton";
import cmIndustryTypeService from "../../services/cmIndustryType.service";
import CommonService from "../../services/common.service";
import b2bHubHomeService from "../../services/b2bHubHome.service";
import {
  buildContractManufacturerFilterOptions,
  EMPTY_CM_FILTERS,
  filterContractManufacturers,
  hasActiveCmFilters,
  mapIndustriesWithManufacturerStats,
} from "../../utils/contractManufacturerBrowseFilters.util";
import { getRatingDisplay } from "../../utils/contractManufacturerCardHelpers";
import { resolveHubImage } from "../../utils/b2bHubHomeMedia";
import { MAX_COMPARE_PRODUCTS } from "../../utils/marketplaceProductCompare.util";
import {
  buildInfraListingsHref,
  getInfraBrowseDepth,
  INFRA_LISTING_TYPE_ITEMS,
  infraListingTypeLabel,
  mapFacilitySubSubType,
  mapFacilitySubType,
  mapFacilityType,
} from "../../constants/infrastructureLeasingBrowse";
import {
  buildAuctionListingsHref,
  buildAuctionBrowseStatsIndex,
  enrichAuctionBrowseItems,
  getAuctionBrowseDepth,
  mapAuctionCategory,
  mapAuctionSubCategory,
} from "../../constants/auctionBrowse";
import { fetchPublishedInfrastructureListings } from "../../services/infrastructureLeasingListing.service";
import {
  buildFacilityTypeCountIndex,
  buildInfraListingStatsIndex,
  enrichInfraBrowseItems,
} from "../../utils/infrastructureBrowseCounts.util";
import { useMasterFacilitySubSubTypes } from "../../hooks/useMasterFacilitySubSubTypes";
import { useMasterFacilitySubTypes } from "../../hooks/useMasterFacilitySubTypes";
import { useMasterFacilityTypes } from "../../hooks/useMasterFacilityTypes";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/home.scss";
import "../../assets/styles/product_marketplace.scss";
import "../../assets/styles/explore_product_categories.scss";

const DEFAULT_PAGE_SIZE = 12;
const PAGE_SIZE_OPTIONS = [12, 24, 36, 48];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
  { value: "name", label: "Name A-Z" },
];

const mapSubCategoryTab = (sub) => ({
  id: sub.sub_category_id ?? sub.id,
  name: sub.sub_category || sub.name,
  imagePath: sub.image_path || null,
  product_count: sub.product_count ?? 0,
  supplier_count: sub.supplier_count ?? 0,
  buyer_request_count: sub.buyer_request_count ?? 0,
  auction_count: sub.auction_count ?? 0,
  rfq_count: sub.rfq_count ?? sub.buyer_request_count ?? 0,
});

const mapSubSubCategoryTab = (sub) => ({
  id: sub.sub_sub_category_id ?? sub.id,
  name: sub.sub_sub_category || sub.name,
  imagePath: sub.image_path || null,
});

const mapCategoryItem = (cat) => ({
  id: cat.category_id ?? cat.id,
  name: cat.category || cat.category_name || cat.name,
  imagePath: cat.image_path || cat.imagePath || null,
  product_count: cat.product_count ?? 0,
  supplier_count: cat.supplier_count ?? 0,
  buyer_request_count: cat.buyer_request_count ?? 0,
  auction_count: cat.auction_count ?? 0,
});

function hasProductDiscoveryFilters(applied) {
  return Boolean(
    applied.q?.trim() ||
    applied.discover === "1" ||
    applied.minPrice ||
    applied.maxPrice ||
    applied.minMoq ||
    applied.maxMoq ||
    applied.brand ||
    applied.location ||
    applied.verifiedOnly === "1",
  );
}

function getBrowseLevel(applied) {
  if (applied.module === "cm" && applied.cmIndustry) return 4;
  const auctionDepth = getAuctionBrowseDepth(applied);
  if (auctionDepth >= 0) {
    if (auctionDepth >= 2) return 4;
    return 1;
  }
  const infraDepth = getInfraBrowseDepth(applied);
  if (infraDepth >= 0) {
    if (infraDepth >= 4) return 4;
    return 1;
  }
  if (hasProductDiscoveryFilters(applied)) return 4;
  if (applied.subSubCategory) return 4;
  if (applied.subCategory) return 4;
  if (applied.category) return 2;
  return 1;
}

function isCmManufacturerBrowse(applied, browseLevel) {
  return applied.module === "cm" && Boolean(applied.cmIndustry) && browseLevel === 4;
}

function isSubcategoryProductBrowse(applied, browseLevel) {
  if (applied.module === "infra" || applied.module === "auctions") return false;
  if (browseLevel !== 4) return false;
  if (applied.subCategory || applied.subSubCategory) return true;
  return hasProductDiscoveryFilters(applied);
}

function CategoryTabThumb({ imagePath, name, fallbackIcon: FallbackIcon = FaBox }) {
  if (imagePath) {
    return (
      <span className="product-marketplace-tabs__icon product-marketplace-tabs__icon--image">
        <img src={resolveHubImage(imagePath)} alt={name} loading="lazy" className="product-marketplace-tabs__img" />
      </span>
    );
  }
  return (
    <span className="product-marketplace-tabs__icon product-marketplace-tabs__icon--fallback">
      <FallbackIcon aria-hidden />
    </span>
  );
}

function CategoryTabsScroller({ children }) {
  const trackRef = useRef(null);

  const scroll = (direction) => {
    trackRef.current?.scrollBy({ left: direction * 280, behavior: "smooth" });
  };

  return (
    <div className="product-marketplace-tabs-scroll">
      <button
        type="button"
        className="product-marketplace-tabs-scroll__arrow product-marketplace-tabs-scroll__arrow--left"
        onClick={() => scroll(-1)}
        aria-label="Scroll left"
      >
        <FaChevronLeft aria-hidden />
      </button>
      <div className="product-marketplace-tabs-scroll__track" ref={trackRef}>
        {children}
      </div>
      <button
        type="button"
        className="product-marketplace-tabs-scroll__arrow product-marketplace-tabs-scroll__arrow--right"
        onClick={() => scroll(1)}
        aria-label="Scroll right"
      >
        <FaChevronRight aria-hidden />
      </button>
    </div>
  );
}

function CategoryTabsRow({ ariaLabel, items, activeId, onSelect, allLabel = "All Categories", showAll = true }) {
  return (
    <CategoryTabsScroller>
      <div className="product-marketplace-tabs" role="tablist" aria-label={ariaLabel}>
        {showAll ? (
          <button
            type="button"
            className={`product-marketplace-tabs__item${!activeId ? " product-marketplace-tabs__item--active" : ""}`}
            onClick={() => onSelect("")}
          >
            <span className="product-marketplace-tabs__icon product-marketplace-tabs__icon--fallback">
              <FaBox aria-hidden />
            </span>
            <span className="product-marketplace-tabs__label">{allLabel}</span>
          </button>
        ) : null}
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`product-marketplace-tabs__item${String(activeId) === String(item.id) ? " product-marketplace-tabs__item--active" : ""}`}
            onClick={() => onSelect(item.id)}
          >
            <CategoryTabThumb imagePath={item.imagePath} name={item.name} />
            <span className="product-marketplace-tabs__label">{item.name}</span>
          </button>
        ))}
      </div>
    </CategoryTabsScroller>
  );
}

function unwrapApiList(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

function readParam(searchParams, key, fallback = "") {
  return searchParams.get(key) ?? fallback;
}

function buildSearchParams(current, updates) {
  const next = new URLSearchParams(current);
  Object.entries(updates).forEach(([key, value]) => {
    if (value == null || value === "") next.delete(key);
    else next.set(key, String(value));
  });
  if (!("page" in updates)) next.delete("page");
  return next;
}

function formatMoqLabel(item) {
  if (item.moq_label) return item.moq_label;
  if (item.moq == null || String(item.moq).trim() === "") return null;
  const unit = item.moq_unit?.trim() || "Units";
  return `MOQ: ${item.moq} ${unit}`;
}

function MarketplaceProductCard({ item, onToggleWish, wished }) {
  const moqLabel = formatMoqLabel(item);
  const detailHref = `/marketplace/products/${item.id}`;

  return (
    <Link to={detailHref} className="product-marketplace-card">
      <div className="product-marketplace-card__media-wrap">
        <MarketplaceAddToCartButton
          item={item}
          iconOnly
          className="product-marketplace-card__cart"
        />
        <button
          type="button"
          className={`product-marketplace-card__wish${wished ? " is-active" : ""}`}
          aria-label="Add to wishlist"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleWish(item.id);
          }}
        >
          <FaHeart aria-hidden />
        </button>
        <div className="product-marketplace-card__media">
          {item.image_path ? (
            <img src={resolveHubImage(item.image_path)} alt={item.title} loading="lazy" />
          ) : (
            <div className="product-marketplace-card__no-image">No image</div>
          )}
        </div>
      </div>
      <div className="product-marketplace-card__body">
        <h3>{item.title}</h3>
        {item.company_name ? <p className="product-marketplace-card__seller">{item.company_name}</p> : null}
        {item.price_label ? <p className="product-marketplace-card__price">{item.price_label}</p> : null}
        {moqLabel ? <p className="product-marketplace-card__moq">{moqLabel}</p> : null}
        {item.location ? (
          <p className="product-marketplace-card__location">
            <FaMapMarkerAlt aria-hidden /> {item.location}
          </p>
        ) : null}
        {item.rating_label ? (
          <p className="product-marketplace-card__rating">
            <FaStar aria-hidden /> {item.rating_label}
          </p>
        ) : null}
        {item.verified ? (
          <span className="product-marketplace-card__verified">
            <FaCheckCircle aria-hidden /> Verified Seller
          </span>
        ) : null}
      </div>
    </Link>
  );
}

export default function ProductMarketplacePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [browseLoading, setBrowseLoading] = useState(true);
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [masterCategories, setMasterCategories] = useState([]);
  const [marketplaceBanners, setMarketplaceBanners] = useState([]);
  const [marketplaceSubcategoryBanners, setMarketplaceSubcategoryBanners] = useState([]);
  const [marketplaceSubcategoryProductBanners, setMarketplaceSubcategoryProductBanners] = useState([]);
  const [browseSubCategories, setBrowseSubCategories] = useState([]);
  const [browseSubSubCategories, setBrowseSubSubCategories] = useState([]);
  const [cmIndustries, setCmIndustries] = useState(
    CM_INDUSTRY_BROWSE_OPTIONS.map((item) => ({ ...item, count: 0 })),
  );
  const [cmRfqCountByCompany, setCmRfqCountByCompany] = useState({});
  const [cmManufacturers, setCmManufacturers] = useState([]);
  const [cmManufacturersLoading, setCmManufacturersLoading] = useState(false);
  const [cmIndustryManufacturers, setCmIndustryManufacturers] = useState([]);
  const [cmIndustryManufacturersLoading, setCmIndustryManufacturersLoading] = useState(false);
  const [cmIndustryFilters, setCmIndustryFilters] = useState(EMPTY_CM_FILTERS);
  const [cmRfqSentCompanyIds, setCmRfqSentCompanyIds] = useState(new Set());
  const [cmIndustryFilterOptions, setCmIndustryFilterOptions] = useState([]);
  const [cmFilters, setCmFilters] = useState(EMPTY_CM_FILTERS);
  const [draftSubCategories, setDraftSubCategories] = useState([]);
  const [draftSubSubCategories, setDraftSubSubCategories] = useState([]);
  const [viewMode, setViewMode] = useState("grid");
  const [compareIds, setCompareIds] = useState([]);
  const [compareProductsById, setCompareProductsById] = useState({});
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const [cmCompareIds, setCmCompareIds] = useState([]);
  const [cmCompareById, setCmCompareById] = useState({});
  const [cmCompareModalOpen, setCmCompareModalOpen] = useState(false);
  const [infraPublishedListings, setInfraPublishedListings] = useState([]);
  const [infraPublishedListingsLoading, setInfraPublishedListingsLoading] = useState(false);
  const [allFacilityTypes, setAllFacilityTypes] = useState([]);
  const [infraMasterChildCounts, setInfraMasterChildCounts] = useState({});
  const [auctionCategories, setAuctionCategories] = useState([]);
  const [auctionSubCategories, setAuctionSubCategories] = useState([]);
  const [auctionSubCategoriesLoading, setAuctionSubCategoriesLoading] = useState(false);
  const [activeAuctions, setActiveAuctions] = useState([]);
  const [auctionBrowseLoading, setAuctionBrowseLoading] = useState(false);
  const [auctionSubCategoryCountByCategory, setAuctionSubCategoryCountByCategory] = useState({});
  const [wishIds, setWishIds] = useState([]);
  const [filterMeta, setFilterMeta] = useState({ brands: [], locations: [] });

  const applied = useMemo(
    () => ({
      category: readParam(searchParams, "category"),
      subCategory: readParam(searchParams, "subCategory"),
      subSubCategory: readParam(searchParams, "subSubCategory"),
      minPrice: readParam(searchParams, "minPrice"),
      maxPrice: readParam(searchParams, "maxPrice"),
      minMoq: readParam(searchParams, "minMoq"),
      maxMoq: readParam(searchParams, "maxMoq"),
      brand: readParam(searchParams, "brand"),
      location: readParam(searchParams, "location"),
      verifiedOnly: readParam(searchParams, "verifiedOnly"),
      discover: readParam(searchParams, "discover"),
      sort: readParam(searchParams, "sort", "newest"),
      q: readParam(searchParams, "q") || readParam(searchParams, "keyword"),
      module: readParam(searchParams, "module"),
      cmIndustry: readParam(searchParams, "cmIndustry"),
      infraType: readParam(searchParams, "infraType"),
      infraCategory: readParam(searchParams, "infraCategory"),
      infraSubCategory: readParam(searchParams, "infraSubCategory"),
      infraSubSubCategory: readParam(searchParams, "infraSubSubCategory"),
      auctionCategory: readParam(searchParams, "auctionCategory"),
      auctionSubCategory: readParam(searchParams, "auctionSubCategory"),
      page: readParam(searchParams, "page", "1"),
      limit: readParam(searchParams, "limit", String(DEFAULT_PAGE_SIZE)),
    }),
    [searchParams],
  );

  const pageSize = Math.min(48, Math.max(1, Number(applied.limit) || DEFAULT_PAGE_SIZE));
  const [draft, setDraft] = useState(applied);

  useEffect(() => {
    setDraft(applied);
  }, [applied]);

  const browseLevel = useMemo(() => getBrowseLevel(applied), [applied]);
  const subcategoryProductBrowse = useMemo(
    () => isSubcategoryProductBrowse(applied, browseLevel),
    [applied, browseLevel],
  );
  const cmManufacturerBrowse = useMemo(
    () => isCmManufacturerBrowse(applied, browseLevel),
    [applied, browseLevel],
  );

  const categories = data?.categories?.length ? data.categories : masterCategories;
  const brands = data?.brands?.length ? data.brands : filterMeta.brands;
  const locations = data?.locations?.length ? data.locations : filterMeta.locations;
  const hasHeaderSearch = Boolean(applied.q?.trim());
  const infraBrowseDepth = useMemo(() => getInfraBrowseDepth(applied), [applied]);
  const auctionBrowseDepth = useMemo(() => getAuctionBrowseDepth(applied), [applied]);
  const isInfraBrowse = infraBrowseDepth >= 0 && infraBrowseDepth < 4;
  const isAuctionBrowse = auctionBrowseDepth >= 0 && auctionBrowseDepth < 2;
  const isCmIndustryBrowse =
    applied.module === "cm" && !applied.cmIndustry && browseLevel < 4 && !isInfraBrowse && !isAuctionBrowse;
  const infraListingType =
    applied.infraType === "property" || applied.infraType === "machinery" ? applied.infraType : null;
  const { list: facilityTypes, loading: loadingFacilityTypes } = useMasterFacilityTypes(
    isInfraBrowse && infraBrowseDepth >= 1 ? infraListingType : null,
  );
  const { list: facilitySubTypes, loading: loadingFacilitySubTypes } = useMasterFacilitySubTypes(
    isInfraBrowse && infraBrowseDepth >= 2 ? applied.infraCategory : null,
  );
  const { list: facilitySubSubTypes, loading: loadingFacilitySubSubTypes } = useMasterFacilitySubSubTypes(
    isInfraBrowse && infraBrowseDepth >= 3 ? applied.infraSubCategory : null,
  );
  const infraAssetCategoryItems = useMemo(
    () => facilityTypes.map(mapFacilityType),
    [facilityTypes],
  );
  const infraSubCategoryItems = useMemo(
    () => facilitySubTypes.map(mapFacilitySubType),
    [facilitySubTypes],
  );
  const infraSubSubCategoryItems = useMemo(
    () => facilitySubSubTypes.map(mapFacilitySubSubType),
    [facilitySubSubTypes],
  );
  const activeInfraAssetCategory = infraAssetCategoryItems.find(
    (item) => String(item.id) === String(applied.infraCategory),
  );
  const activeInfraSubCategory = infraSubCategoryItems.find(
    (item) => String(item.id) === String(applied.infraSubCategory),
  );
  const pagination = data?.pagination || { page: 1, total: 0, total_pages: 0, from: 0, to: 0, limit: pageSize };

  const activeCategory =
    masterCategories.find((c) => String(c.id) === String(applied.category)) ||
    categories.find((c) => String(c.id) === String(applied.category));
  const activeSubCategory = browseSubCategories.find(
    (c) => String(c.id ?? c.sub_category_id) === String(applied.subCategory),
  ) || draftSubCategories.find((c) => String(c.sub_category_id ?? c.id) === String(applied.subCategory));
  const activeSubSubCategory = browseSubSubCategories.find(
    (c) => String(c.id ?? c.sub_sub_category_id) === String(applied.subSubCategory),
  ) || draftSubSubCategories.find(
    (c) => String(c.sub_sub_category_id ?? c.id) === String(applied.subSubCategory),
  );

  useEffect(() => {
    if (getInfraBrowseDepth(applied) !== 4) return;
    navigate(buildInfraListingsHref(applied), { replace: true });
  }, [applied, navigate]);

  useEffect(() => {
    if (getAuctionBrowseDepth(applied) !== 2) return;
    navigate(buildAuctionListingsHref(applied), { replace: true });
  }, [applied, navigate]);

  useEffect(() => {
    if (!isInfraBrowse) {
      setInfraPublishedListings([]);
      setAllFacilityTypes([]);
      return undefined;
    }
    let cancelled = false;
    setInfraPublishedListingsLoading(true);
    Promise.all([
      fetchPublishedInfrastructureListings(500),
      CommonService.get("/master/facility-types", {}, { silent: true }),
    ])
      .then(([listingsRes, facilityTypesRes]) => {
        if (cancelled) return;
        const items = Array.isArray(listingsRes?.data?.items) ? listingsRes.data.items : [];
        const facilityRows = Array.isArray(facilityTypesRes?.data) ? facilityTypesRes.data : [];
        setInfraPublishedListings(items);
        setAllFacilityTypes(facilityRows);
      })
      .catch(() => {
        if (!cancelled) {
          setInfraPublishedListings([]);
          setAllFacilityTypes([]);
        }
      })
      .finally(() => {
        if (!cancelled) setInfraPublishedListingsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isInfraBrowse]);

  useEffect(() => {
    if (!isInfraBrowse || infraBrowseDepth !== 1 || !facilityTypes.length) {
      setInfraMasterChildCounts({});
      return undefined;
    }
    let cancelled = false;
    Promise.all(
      facilityTypes.map(async (row) => {
        const typeId = row.facility_type_id;
        try {
          const res = await CommonService.get(`/master/facility-sub-types/${typeId}`, {}, { silent: true });
          const subs = Array.isArray(res?.data) ? res.data : [];
          return [String(typeId), subs.length];
        } catch {
          return [String(typeId), 0];
        }
      }),
    ).then((entries) => {
      if (!cancelled) {
        setInfraMasterChildCounts(Object.fromEntries(entries));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isInfraBrowse, infraBrowseDepth, facilityTypes]);

  useEffect(() => {
    if (!isInfraBrowse || infraBrowseDepth !== 2 || applied.infraType !== "machinery" || !facilitySubTypes.length) {
      if (infraBrowseDepth !== 2) return undefined;
      if (applied.infraType !== "machinery") {
        setInfraMasterChildCounts({});
      }
      return undefined;
    }
    let cancelled = false;
    Promise.all(
      facilitySubTypes.map(async (row) => {
        const subTypeId = row.facility_sub_type_id;
        try {
          const res = await CommonService.get(
            `/master/facility-sub-sub-types/${subTypeId}`,
            {},
            { silent: true },
          );
          const subSubs = Array.isArray(res?.data) ? res.data : [];
          return [String(subTypeId), subSubs.length];
        } catch {
          return [String(subTypeId), 0];
        }
      }),
    ).then((entries) => {
      if (!cancelled) {
        setInfraMasterChildCounts(Object.fromEntries(entries));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isInfraBrowse, infraBrowseDepth, applied.infraType, facilitySubTypes]);

  useEffect(() => {
    if (!isAuctionBrowse) {
      setAuctionCategories([]);
      setAuctionSubCategories([]);
      setActiveAuctions([]);
      return undefined;
    }
    let cancelled = false;
    setAuctionBrowseLoading(true);
    Promise.all([
      CommonService.get(`${API_URLS.AUCTION.BASE}/categories`, {}, { silent: true }),
      CommonService.get(API_URLS.AUCTION.ACTIVE, {}, { silent: true }),
    ])
      .then(([categoriesRes, auctionsRes]) => {
        if (cancelled) return;
        const categories = Array.isArray(categoriesRes?.data) ? categoriesRes.data : [];
        const auctions = Array.isArray(auctionsRes?.data) ? auctionsRes.data : [];
        setAuctionCategories(categories);
        setActiveAuctions(auctions);
      })
      .catch(() => {
        if (!cancelled) {
          setAuctionCategories([]);
          setActiveAuctions([]);
        }
      })
      .finally(() => {
        if (!cancelled) setAuctionBrowseLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuctionBrowse]);

  useEffect(() => {
    if (!isAuctionBrowse || !auctionCategories.length) {
      setAuctionSubCategoryCountByCategory({});
      return undefined;
    }
    let cancelled = false;
    Promise.all(
      auctionCategories.map((cat) =>
        CommonService.get(
          `${API_URLS.AUCTION.BASE}/categories/${cat.category_id ?? cat.id}/sub-categories`,
          {},
          { silent: true },
        ).then((res) => ({
          categoryId: String(cat.category_id ?? cat.id),
          count: Array.isArray(res?.data) ? res.data.length : 0,
        })),
      ),
    )
      .then((entries) => {
        if (cancelled) return;
        setAuctionSubCategoryCountByCategory(Object.fromEntries(entries.map((e) => [e.categoryId, e.count])));
      })
      .catch(() => {
        if (!cancelled) setAuctionSubCategoryCountByCategory({});
      });
    return () => {
      cancelled = true;
    };
  }, [isAuctionBrowse, auctionCategories]);

  useEffect(() => {
    if (!isAuctionBrowse || !applied.auctionCategory) {
      setAuctionSubCategories([]);
      setAuctionSubCategoriesLoading(false);
      return undefined;
    }
    let cancelled = false;
    setAuctionSubCategoriesLoading(true);
    CommonService.get(
      `${API_URLS.AUCTION.BASE}/categories/${applied.auctionCategory}/sub-categories`,
      {},
      { silent: true },
    )
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        setAuctionSubCategories(list);
        if (list.length === 0 && auctionBrowseDepth === 1) {
          navigate(buildAuctionListingsHref(applied), { replace: true });
        }
      })
      .catch(() => {
        if (!cancelled) setAuctionSubCategories([]);
      })
      .finally(() => {
        if (!cancelled) setAuctionSubCategoriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuctionBrowse, applied.auctionCategory, auctionBrowseDepth, applied, navigate]);

  const infraListingStats = useMemo(
    () => buildInfraListingStatsIndex(infraPublishedListings),
    [infraPublishedListings],
  );
  const infraFacilityTypeCounts = useMemo(
    () => buildFacilityTypeCountIndex(allFacilityTypes),
    [allFacilityTypes],
  );

  const infraBrowseItems = useMemo(() => {
    if (!isInfraBrowse) return [];
    let baseItems = [];
    if (infraBrowseDepth === 0) baseItems = INFRA_LISTING_TYPE_ITEMS;
    else if (infraBrowseDepth === 1) baseItems = infraAssetCategoryItems;
    else if (infraBrowseDepth === 2) baseItems = infraSubCategoryItems;
    else baseItems = infraSubSubCategoryItems;

    const enriched = enrichInfraBrowseItems({
      depth: infraBrowseDepth,
      items: baseItems,
      listingStats: infraListingStats,
      facilityTypeCounts: infraFacilityTypeCounts,
      infraType: applied.infraType,
    });

    if (infraBrowseDepth === 1 || (infraBrowseDepth === 2 && applied.infraType === "machinery")) {
      return enriched.map((item) => ({
        ...item,
        supplier_count: Math.max(
          Number(item.supplier_count) || 0,
          Number(infraMasterChildCounts[String(item.id)]) || 0,
        ),
      }));
    }
    return enriched;
  }, [
    isInfraBrowse,
    infraBrowseDepth,
    infraAssetCategoryItems,
    infraSubCategoryItems,
    infraSubSubCategoryItems,
    infraListingStats,
    infraFacilityTypeCounts,
    infraMasterChildCounts,
    applied.infraType,
  ]);

  const auctionCategoryItems = useMemo(
    () => auctionCategories.map(mapAuctionCategory),
    [auctionCategories],
  );
  const auctionSubCategoryItems = useMemo(
    () => auctionSubCategories.map(mapAuctionSubCategory),
    [auctionSubCategories],
  );
  const activeAuctionCategory = auctionCategoryItems.find(
    (item) => String(item.id) === String(applied.auctionCategory),
  );
  const auctionBrowseStatsIndex = useMemo(
    () => buildAuctionBrowseStatsIndex(activeAuctions, auctionSubCategoryCountByCategory),
    [activeAuctions, auctionSubCategoryCountByCategory],
  );
  const auctionBrowseItems = useMemo(() => {
    if (!isAuctionBrowse) return [];
    const baseItems = auctionBrowseDepth === 0 ? auctionCategoryItems : auctionSubCategoryItems;
    return enrichAuctionBrowseItems({
      items: baseItems,
      depth: auctionBrowseDepth,
      statsIndex: auctionBrowseStatsIndex,
    });
  }, [
    isAuctionBrowse,
    auctionBrowseDepth,
    auctionCategoryItems,
    auctionSubCategoryItems,
    auctionBrowseStatsIndex,
  ]);

  useEffect(() => {
    let cancelled = false;
    setBrowseLoading(true);
    b2bHubHomeService
      .getProductCategories()
      .then((res) => {
        if (!cancelled) {
          const payload = res?.data ?? res ?? {};
          setMasterCategories((payload.categories || []).map(mapCategoryItem));
          setMarketplaceBanners(payload.marketplace_banners || []);
          setMarketplaceSubcategoryBanners(payload.marketplace_subcategory_banners || []);
          setMarketplaceSubcategoryProductBanners(payload.marketplace_subcategory_product_banners || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          CommonService.get("/master/master-categories", {}, { silent: true })
            .then((res) => setMasterCategories(unwrapApiList(res).map(mapCategoryItem)))
            .catch(() => setMasterCategories([]));
          setMarketplaceBanners([]);
          setMarketplaceSubcategoryBanners([]);
          setMarketplaceSubcategoryProductBanners([]);
        }
      })
      .finally(() => {
        if (!cancelled) setBrowseLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    b2bHubHomeService
      .getHome()
      .then((res) => {
        if (cancelled) return;
        const fromApi = Array.isArray(res?.data?.contract_manufacturer_industries)
          ? res.data.contract_manufacturer_industries
          : [];
        const rfqByCompany = res?.data?.cm_rfq_count_by_company;
        setCmRfqCountByCompany(
          rfqByCompany && typeof rfqByCompany === "object" && !Array.isArray(rfqByCompany)
            ? rfqByCompany
            : {},
        );
        setCmIndustries(
          fromApi.length
            ? fromApi
            : CM_INDUSTRY_BROWSE_OPTIONS.map((item) => ({ ...item, count: 0 })),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setCmIndustries(CM_INDUSTRY_BROWSE_OPTIONS.map((item) => ({ ...item, count: 0 })));
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isCmIndustryBrowse) return undefined;
    let cancelled = false;
    setCmManufacturersLoading(true);
    CommonService.get(API_URLS.CONTRACT_MANUFACTURER.LIST, { page: 1, limit: 200 }, { silent: true })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data ?? res ?? {};
        setCmManufacturers(Array.isArray(payload.items) ? payload.items : []);
      })
      .catch(() => {
        if (!cancelled) setCmManufacturers([]);
      })
      .finally(() => {
        if (!cancelled) setCmManufacturersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isCmIndustryBrowse]);

  useEffect(() => {
    if (!cmManufacturerBrowse || !applied.cmIndustry) {
      setCmIndustryManufacturers([]);
      return undefined;
    }
    let cancelled = false;
    setCmIndustryManufacturersLoading(true);
    const requests = [
      CommonService.get(
        API_URLS.CONTRACT_MANUFACTURER.LIST,
        { industry: applied.cmIndustry, page: 1, limit: 200 },
        { silent: true },
      ),
    ];
    if (StorageService.isAuthenticated()) {
      requests.push(
        CommonService.get(API_URLS.CONTRACT_MANUFACTURER.MY_RFQ_SUBMISSIONS, { limit: 500, offset: 0 }, { silent: true }),
      );
    }
    Promise.allSettled(requests)
      .then(([listRes, submissionsRes]) => {
        if (cancelled) return;
        if (listRes.status === "fulfilled") {
          const payload = listRes.value?.data ?? listRes.value ?? {};
          setCmIndustryManufacturers(Array.isArray(payload.items) ? payload.items : []);
        } else {
          setCmIndustryManufacturers([]);
        }
        if (submissionsRes?.status === "fulfilled") {
          const submissionsData = submissionsRes.value?.data ?? submissionsRes.value ?? {};
          const submissionItems = Array.isArray(submissionsData?.items) ? submissionsData.items : [];
          setCmRfqSentCompanyIds(
            new Set(
              submissionItems
                .map((row) => Number(row?.company_id))
                .filter((id) => Number.isFinite(id) && id > 0),
            ),
          );
        } else {
          setCmRfqSentCompanyIds(new Set());
        }
      })
      .finally(() => {
        if (!cancelled) setCmIndustryManufacturersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cmManufacturerBrowse, applied.cmIndustry]);

  useEffect(() => {
    if (!isCmIndustryBrowse && !cmManufacturerBrowse) return undefined;
    let cancelled = false;
    cmIndustryTypeService
      .listActive()
      .then((res) => {
        if (cancelled) return;
        const items = Array.isArray(res?.data?.items) ? res.data.items : [];
        if (items.length) {
          setCmIndustryFilterOptions(items.map((row) => ({ value: row.name, label: row.name })));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isCmIndustryBrowse, cmManufacturerBrowse]);

  useEffect(() => {
    if (!isCmIndustryBrowse) {
      setCmFilters(EMPTY_CM_FILTERS);
    }
  }, [isCmIndustryBrowse]);

  useEffect(() => {
    if (!cmManufacturerBrowse) {
      setCmIndustryFilters(EMPTY_CM_FILTERS);
    }
  }, [cmManufacturerBrowse]);

  useEffect(() => {
    let cancelled = false;
    b2bHubHomeService
      .getMarketplace({ page: 1, limit: 1 })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data ?? res ?? {};
        setFilterMeta({
          brands: payload.brands || [],
          locations: payload.locations || [],
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchMarketplace = useCallback(async () => {
    if (browseLevel !== 4) return;
    setLoading(true);
    try {
      const res = await b2bHubHomeService.getMarketplace({
        category: applied.category || undefined,
        subCategory: applied.subCategory || undefined,
        subSubCategory: applied.subSubCategory || undefined,
        minPrice: applied.minPrice || undefined,
        maxPrice: applied.maxPrice || undefined,
        minMoq: applied.minMoq || undefined,
        maxMoq: applied.maxMoq || undefined,
        brand: applied.brand || undefined,
        location: applied.location || undefined,
        verifiedOnly: applied.verifiedOnly === "1" ? "1" : undefined,
        sort: ["best_match", "recent", "delivery", "rated", "moq_asc"].includes(applied.sort)
          ? "newest"
          : applied.sort || "newest",
        q: applied.q || undefined,
        page: applied.page || 1,
        limit: pageSize,
      });
      const payload = res?.data ?? res ?? {};
      setData(payload);
      setProducts(payload.products || []);
    } catch {
      toast.error("Failed to load marketplace products");
      setData(null);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [applied, browseLevel, pageSize]);

  useEffect(() => {
    fetchMarketplace();
  }, [fetchMarketplace]);

  useEffect(() => {
    if (browseLevel < 2 || !applied.category) {
      setBrowseSubCategories([]);
      return undefined;
    }
    let cancelled = false;
    setBrowseLoading(true);
    CommonService.get(`/master/master-sub-categories/${applied.category}`, {}, { silent: true })
      .then((res) => {
        if (!cancelled) setBrowseSubCategories(unwrapApiList(res).map(mapSubCategoryTab));
      })
      .catch(() => {
        if (!cancelled) setBrowseSubCategories([]);
      })
      .finally(() => {
        if (!cancelled) setBrowseLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [browseLevel, applied.category]);

  useEffect(() => {
    if (!applied.subCategory) {
      setBrowseSubSubCategories([]);
      return undefined;
    }
    let cancelled = false;
    setBrowseLoading(true);
    CommonService.get(`/master/master-sub-sub-categories/${applied.subCategory}`, {}, { silent: true })
      .then((res) => {
        if (!cancelled) setBrowseSubSubCategories(unwrapApiList(res).map(mapSubSubCategoryTab));
      })
      .catch(() => {
        if (!cancelled) setBrowseSubSubCategories([]);
      })
      .finally(() => {
        if (!cancelled) setBrowseLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [applied.subCategory]);

  useEffect(() => {
    if (!draft.category) return;
    CommonService.get(`/master/master-sub-categories/${draft.category}`)
      .then((res) => setDraftSubCategories(unwrapApiList(res)))
      .catch(() => {});
  }, [draft.category]);

  useEffect(() => {
    if (!draft.subCategory) return;
    CommonService.get(`/master/master-sub-sub-categories/${draft.subCategory}`)
      .then((res) => setDraftSubSubCategories(unwrapApiList(res)))
      .catch(() => {});
  }, [draft.subCategory]);

  const applyFilters = () => {
    setSearchParams(
      buildSearchParams(searchParams, {
        category: draft.category,
        subCategory: draft.subCategory,
        subSubCategory: draft.subSubCategory,
        minPrice: draft.minPrice,
        maxPrice: draft.maxPrice,
        minMoq: draft.minMoq,
        maxMoq: draft.maxMoq,
        brand: draft.brand,
        location: draft.location,
        verifiedOnly: draft.verifiedOnly === "1" ? "1" : "",
        sort: draft.sort,
        q: draft.q,
        discover: "1",
        page: "",
      }),
    );
  };

  const clearFilters = () => {
    setSearchParams(
      buildSearchParams(searchParams, {
        minPrice: "",
        maxPrice: "",
        minMoq: "",
        maxMoq: "",
        brand: "",
        location: "",
        verifiedOnly: "",
        q: "",
        discover: "",
        sort: "newest",
        page: "",
      }),
    );
  };

  const submitSearch = (query) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    setSearchParams(
      buildSearchParams(searchParams, {
        q: trimmed,
        discover: "1",
        page: "",
      }),
    );
  };

  const clearCategoryPath = () => {
    patchFilters({ category: "", subCategory: "", subSubCategory: "" });
  };

  const patchFilters = (updates) => {
    setSearchParams(buildSearchParams(searchParams, { ...updates, page: "" }));
  };

  const patchBrowseNavigation = (updates) => {
    patchFilters({ ...updates, discover: "" });
  };

  const selectMarketplaceModule = (moduleKey) => {
    if (moduleKey === "cm") {
      setCmFilters(EMPTY_CM_FILTERS);
      setCmIndustryFilters(EMPTY_CM_FILTERS);
      patchBrowseNavigation({
        module: "cm",
        cmIndustry: "",
        infraType: "",
        infraCategory: "",
        infraSubCategory: "",
        infraSubSubCategory: "",
        auctionCategory: "",
        auctionSubCategory: "",
        category: "",
        subCategory: "",
        subSubCategory: "",
        q: "",
      });
      return;
    }
    if (moduleKey === "infra") {
      patchBrowseNavigation({
        module: "infra",
        cmIndustry: "",
        infraType: "",
        infraCategory: "",
        infraSubCategory: "",
        infraSubSubCategory: "",
        auctionCategory: "",
        auctionSubCategory: "",
        category: "",
        subCategory: "",
        subSubCategory: "",
        q: "",
      });
      return;
    }
    if (moduleKey === "auctions") {
      patchBrowseNavigation({
        module: "auctions",
        cmIndustry: "",
        infraType: "",
        infraCategory: "",
        infraSubCategory: "",
        infraSubSubCategory: "",
        auctionCategory: "",
        auctionSubCategory: "",
        category: "",
        subCategory: "",
        subSubCategory: "",
        q: "",
      });
      return;
    }
    if (moduleKey === "products") {
      patchBrowseNavigation({
        module: "",
        cmIndustry: "",
        infraType: "",
        infraCategory: "",
        infraSubCategory: "",
        infraSubSubCategory: "",
        auctionCategory: "",
        auctionSubCategory: "",
        category: "",
        subCategory: "",
        subSubCategory: "",
        q: "",
      });
    }
  };

  const clearCmFilters = () => {
    setCmFilters(EMPTY_CM_FILTERS);
  };

  const clearCmIndustryFilters = () => {
    setCmIndustryFilters(EMPTY_CM_FILTERS);
  };

  const selectCategory = (categoryId) => {
    patchBrowseNavigation({
      category: categoryId ? String(categoryId) : "",
      subCategory: "",
      subSubCategory: "",
    });
  };

  const selectSubCategory = (subCategoryId) => {
    patchBrowseNavigation({
      subCategory: subCategoryId ? String(subCategoryId) : "",
      subSubCategory: "",
    });
  };

  const selectSubSubCategory = (subSubCategoryId) => {
    patchBrowseNavigation({
      subSubCategory: subSubCategoryId ? String(subSubCategoryId) : "",
    });
  };

  const goBack = () => {
    if (isAuctionBrowse) {
      if (auctionBrowseDepth === 1) {
        patchBrowseNavigation({ auctionCategory: "", auctionSubCategory: "" });
        return;
      }
      if (auctionBrowseDepth === 0) {
        patchBrowseNavigation({
          module: "",
          auctionCategory: "",
          auctionSubCategory: "",
        });
      }
      return;
    }
    if (isInfraBrowse) {
      if (infraBrowseDepth === 3) {
        patchBrowseNavigation({ infraSubCategory: "", infraSubSubCategory: "" });
        return;
      }
      if (infraBrowseDepth === 2) {
        patchBrowseNavigation({ infraSubCategory: "", infraSubSubCategory: "" });
        return;
      }
      if (infraBrowseDepth === 1) {
        patchBrowseNavigation({
          infraCategory: "",
          infraSubCategory: "",
          infraSubSubCategory: "",
        });
        return;
      }
      if (infraBrowseDepth === 0) {
        patchBrowseNavigation({
          module: "",
          infraType: "",
          infraCategory: "",
          infraSubCategory: "",
          infraSubSubCategory: "",
        });
      }
      return;
    }
    if (isCmIndustryBrowse) {
      patchBrowseNavigation({ module: "" });
      return;
    }
    if (cmManufacturerBrowse) {
      patchBrowseNavigation({ cmIndustry: "" });
      return;
    }
    if (browseLevel === 4) {
      if (hasHeaderSearch && !applied.category && !applied.subCategory && !applied.subSubCategory) {
        patchFilters({ q: "", discover: "" });
        return;
      }
      if (hasProductDiscoveryFilters(applied) && !applied.subSubCategory) {
        clearFilters();
        return;
      }
      if (applied.subSubCategory) {
        patchBrowseNavigation({ subSubCategory: "" });
        return;
      }
      if (applied.subCategory) {
        patchBrowseNavigation({ subCategory: "", subSubCategory: "" });
        return;
      }
      return;
    }
    if (browseLevel === 2) {
      patchBrowseNavigation({ category: "", subCategory: "", subSubCategory: "" });
      return;
    }
    if (browseLevel === 1) {
      navigate("/home");
    }
  };

  const breadcrumbHref = (params) => {
    const next = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) next.set(key, String(value));
    });
    const qs = next.toString();
    return qs ? `/marketplace/products?${qs}` : "/marketplace/products";
  };

  const navigateCmManufacturerBreadcrumb = (target) => {
    if (target === "home") {
      navigate("/home");
      return;
    }
    setCmIndustryFilters(EMPTY_CM_FILTERS);
    if (target === "categories") {
      patchBrowseNavigation({
        module: "",
        cmIndustry: "",
        infraType: "",
        infraCategory: "",
        infraSubCategory: "",
        infraSubSubCategory: "",
        auctionCategory: "",
        auctionSubCategory: "",
        category: "",
        subCategory: "",
        subSubCategory: "",
        q: "",
        sort: "",
      });
      return;
    }
    if (target === "cm-industries") {
      patchBrowseNavigation({
        module: "cm",
        cmIndustry: "",
        auctionCategory: "",
        auctionSubCategory: "",
        category: "",
        subCategory: "",
        subSubCategory: "",
        q: "",
        sort: "",
      });
    }
  };

  const handleBrowseSelect = (id) => {
    if (isAuctionBrowse) {
      if (auctionBrowseDepth === 0) {
        patchBrowseNavigation({
          auctionCategory: String(id),
          auctionSubCategory: "",
        });
        return;
      }
      if (auctionBrowseDepth === 1) {
        patchBrowseNavigation({ auctionSubCategory: String(id) });
      }
      return;
    }
    if (isInfraBrowse) {
      if (infraBrowseDepth === 0) {
        patchBrowseNavigation({
          infraType: String(id),
          infraCategory: "",
          infraSubCategory: "",
          infraSubSubCategory: "",
        });
        return;
      }
      if (infraBrowseDepth === 1) {
        patchBrowseNavigation({
          infraCategory: String(id),
          infraSubCategory: "",
          infraSubSubCategory: "",
        });
        return;
      }
      if (infraBrowseDepth === 2) {
        if (applied.infraType === "property") {
          navigate(buildInfraListingsHref({ ...applied, infraSubCategory: String(id) }));
          return;
        }
        patchBrowseNavigation({
          infraSubCategory: String(id),
          infraSubSubCategory: "",
        });
        return;
      }
      if (infraBrowseDepth === 3) {
        navigate(buildInfraListingsHref({ ...applied, infraSubSubCategory: String(id) }));
      }
      return;
    }
    if (isCmIndustryBrowse) {
      setCmIndustryFilters(EMPTY_CM_FILTERS);
      patchBrowseNavigation({ cmIndustry: String(id), page: "1", sort: "relevance" });
      return;
    }
    if (browseLevel === 1) selectCategory(id);
    else if (browseLevel === 2) selectSubCategory(id);
  };

  const submitCmManufacturerSearch = (query) => {
    setCmIndustryFilters((current) => ({ ...current, search: query.trim() }));
    setSearchParams(buildSearchParams(searchParams, { page: "1" }));
  };

  const handleCmManufacturerSortChange = (sortValue) => {
    setSearchParams(buildSearchParams(searchParams, { sort: sortValue, page: "1" }));
  };

  const submitSubcategorySearch = (query) => {
    const trimmed = query.trim();
    setSearchParams(
      buildSearchParams(searchParams, {
        q: trimmed,
        discover: trimmed ? "1" : "",
        page: "",
      }),
    );
  };

  const handleSubcategorySortChange = (option) => {
    setSearchParams(
      buildSearchParams(searchParams, {
        sort: option.value,
        verifiedOnly: option.verifiedOnly ? "1" : "",
        page: "",
      }),
    );
  };

  const toggleCompare = (id) => {
    setCompareIds((prev) => {
      if (prev.includes(id)) {
        const next = prev.filter((x) => x !== id);
        setCompareProductsById((map) => {
          const nextMap = { ...map };
          delete nextMap[id];
          return nextMap;
        });
        if (next.length < 2) setCompareModalOpen(false);
        return next;
      }

      if (prev.length >= MAX_COMPARE_PRODUCTS) {
        toast.info(`You can compare up to ${MAX_COMPARE_PRODUCTS} products at a time.`);
        return prev;
      }

      const product = products.find((item) => item.id === id);
      if (product) {
        setCompareProductsById((map) => ({ ...map, [id]: product }));
      }

      const next = [...prev, id];
      return next;
    });
  };

  const toggleCmCompare = (companyId) => {
    const id = Number(companyId);
    if (!Number.isFinite(id) || id <= 0) return;

    setCmCompareIds((prev) => {
      if (prev.includes(id)) {
        const next = prev.filter((x) => x !== id);
        setCmCompareById((map) => {
          const nextMap = { ...map };
          delete nextMap[id];
          return nextMap;
        });
        if (next.length < 2) setCmCompareModalOpen(false);
        return next;
      }

      if (prev.length >= MAX_COMPARE_PRODUCTS) {
        toast.info(`You can compare up to ${MAX_COMPARE_PRODUCTS} manufacturers at a time.`);
        return prev;
      }

      const manufacturer = filteredCmIndustryManufacturers.find((item) => Number(item.company_id) === id);
      if (manufacturer) {
        setCmCompareById((map) => ({ ...map, [id]: manufacturer }));
      }

      return [...prev, id];
    });
  };

  const compareProducts = useMemo(
    () => compareIds.map((id) => compareProductsById[id]).filter(Boolean),
    [compareIds, compareProductsById],
  );

  const compareManufacturers = useMemo(
    () => cmCompareIds.map((id) => cmCompareById[id]).filter(Boolean),
    [cmCompareIds, cmCompareById],
  );

  const goToPage = (page) => {
    setSearchParams(buildSearchParams(searchParams, { page: String(page) }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const changePageSize = (size) => {
    setSearchParams(buildSearchParams(searchParams, { limit: String(size), page: "1" }));
  };

  const toggleWish = (id) => {
    setWishIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const showingTotal = pagination.total || 0;

  const pageNumbers = useMemo(() => {
    const totalPages = pagination.total_pages || 0;
    const current = Number(applied.page || 1);
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [1];
    if (current > 3) pages.push("…");
    for (let p = Math.max(2, current - 1); p <= Math.min(totalPages - 1, current + 1); p += 1) pages.push(p);
    if (current < totalPages - 2) pages.push("…");
    pages.push(totalPages);
    return pages;
  }, [pagination.total_pages, applied.page]);

  const resultBreadcrumbParts = [
    activeCategory?.name,
    activeSubCategory?.sub_category || activeSubCategory?.name,
    activeSubSubCategory?.sub_sub_category || activeSubSubCategory?.name,
  ].filter(Boolean);

  const categoryOptions = categories.map((cat) => ({ value: cat.id, label: cat.name }));
  const subCategoryOptions = draftSubCategories.map((sub) => ({
    value: sub.sub_category_id ?? sub.id,
    label: sub.sub_category || sub.name,
  }));
  const subSubCategoryOptions = draftSubSubCategories.map((sub) => ({
    value: sub.sub_sub_category_id ?? sub.id,
    label: sub.sub_sub_category || sub.name,
  }));

  const categoryTabItems = useMemo(() => masterCategories, [masterCategories]);
  const subCategoryTabItems = useMemo(() => browseSubCategories, [browseSubCategories]);
  const subSubCategoryTabItems = useMemo(() => browseSubSubCategories, [browseSubSubCategories]);
  const filteredCmManufacturers = useMemo(
    () => filterContractManufacturers(cmManufacturers, cmFilters, cmIndustryFilterOptions),
    [cmManufacturers, cmFilters, cmIndustryFilterOptions],
  );

  const cmFilterOptions = useMemo(
    () => buildContractManufacturerFilterOptions(cmManufacturers, cmIndustryFilterOptions),
    [cmManufacturers, cmIndustryFilterOptions],
  );

  const cmIndustryItems = useMemo(() => {
    const activeFilters = hasActiveCmFilters(cmFilters);
    const industriesWithStats = activeFilters
      ? mapIndustriesWithManufacturerStats(
          cmIndustries,
          filteredCmManufacturers,
          cmIndustryFilterOptions,
          { useApiCounts: false, rfqCountByCompany: cmRfqCountByCompany },
        )
      : mapIndustriesWithManufacturerStats(
          cmIndustries,
          cmManufacturers,
          cmIndustryFilterOptions,
          { useApiCounts: true, rfqCountByCompany: cmRfqCountByCompany },
        );

    const selectedIndustrySlugs = new Set(
      (cmFilters.selectedIndustryFilters || []).map((value) => industryTypeToSlug(value)),
    );

    return [...industriesWithStats]
      .filter((item) => {
        if (selectedIndustrySlugs.size > 0 && !selectedIndustrySlugs.has(item.slug)) {
          return false;
        }
        if (activeFilters) {
          return Number(item.count) > 0;
        }
        return true;
      })
      .sort(
        (a, b) =>
          (Number(b.count) || 0) - (Number(a.count) || 0) ||
          String(a.name).localeCompare(String(b.name)),
      )
      .map((item) => ({
        id: item.slug,
        name: item.name,
        imagePath: item.image_path || CM_INDUSTRY_IMAGE_BY_SLUG[item.slug] || null,
        count: item.count ?? 0,
        supplier_count: item.supplier_count ?? item.count ?? 0,
        product_count: item.product_count ?? 0,
        rfq_count: item.rfq_count ?? 0,
        verified_count: item.verified_count ?? item.buyer_request_count ?? 0,
        buyer_request_count: item.verified_count ?? item.buyer_request_count ?? 0,
      }));
  }, [
    cmIndustries,
    cmFilters,
    cmManufacturers,
    filteredCmManufacturers,
    cmIndustryFilterOptions,
    cmRfqCountByCompany,
  ]);

  const activeCmIndustry = useMemo(() => {
    const fromItems = cmIndustryItems.find((item) => item.id === applied.cmIndustry);
    if (fromItems) {
      return {
        slug: fromItems.id,
        name: fromItems.name,
        count: fromItems.count,
        supplier_count: fromItems.supplier_count,
        product_count: fromItems.product_count,
        rfq_count: fromItems.rfq_count,
        verified_count: fromItems.verified_count,
        buyer_request_count: fromItems.buyer_request_count,
        image_path: fromItems.imagePath,
      };
    }
    const fromApi = cmIndustries.find((item) => item.slug === applied.cmIndustry);
    if (fromApi) return fromApi;
    const label = industrySlugToLabel(applied.cmIndustry);
    return label
      ? { slug: applied.cmIndustry, name: label, count: 0 }
      : { slug: applied.cmIndustry, name: applied.cmIndustry, count: 0 };
  }, [cmIndustryItems, cmIndustries, applied.cmIndustry]);

  const filteredCmIndustryManufacturers = useMemo(() => {
    const filtered = filterContractManufacturers(
      cmIndustryManufacturers,
      cmIndustryFilters,
      cmIndustryFilterOptions,
    );
    const sortValue = applied.sort || "relevance";
    return [...filtered].sort((a, b) => {
      if (sortValue === "name") {
        return String(a.company_name || "").localeCompare(String(b.company_name || ""), undefined, {
          sensitivity: "base",
        });
      }
      if (sortValue === "rating") {
        return (Number(getRatingDisplay(b)) || 0) - (Number(getRatingDisplay(a)) || 0);
      }
      return 0;
    });
  }, [cmIndustryManufacturers, cmIndustryFilters, cmIndustryFilterOptions, applied.sort]);

  const cmIndustryManufacturerPagination = useMemo(() => {
    const total = filteredCmIndustryManufacturers.length;
    const page = Math.max(1, Number(applied.page) || 1);
    const limit = pageSize;
    const total_pages = Math.max(1, Math.ceil(total / limit) || 1);
    const safePage = Math.min(page, total_pages);
    const from = total === 0 ? 0 : (safePage - 1) * limit + 1;
    const to = Math.min(safePage * limit, total);
    return { page: safePage, total, total_pages, from, to, limit };
  }, [filteredCmIndustryManufacturers.length, applied.page, pageSize]);

  const paginatedCmIndustryManufacturers = useMemo(() => {
    const page = cmIndustryManufacturerPagination.page;
    const start = (page - 1) * pageSize;
    return filteredCmIndustryManufacturers.slice(start, start + pageSize);
  }, [filteredCmIndustryManufacturers, cmIndustryManufacturerPagination.page, pageSize]);

  const cmIndustryManufacturerFilterOptions = useMemo(
    () => buildContractManufacturerFilterOptions(cmIndustryManufacturers, cmIndustryFilterOptions),
    [cmIndustryManufacturers, cmIndustryFilterOptions],
  );

  const cmIndustryManufacturerPageNumbers = useMemo(() => {
    const totalPages = cmIndustryManufacturerPagination.total_pages || 0;
    const current = Number(cmIndustryManufacturerPagination.page || 1);
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [1];
    if (current > 3) pages.push("…");
    for (let p = Math.max(2, current - 1); p <= Math.min(totalPages - 1, current + 1); p += 1) pages.push(p);
    if (current < totalPages - 2) pages.push("…");
    pages.push(totalPages);
    return pages;
  }, [cmIndustryManufacturerPagination.total_pages, cmIndustryManufacturerPagination.page]);

  const exploreBrowseCopy = useMemo(() => {
    if (isAuctionBrowse) {
      if (auctionBrowseDepth === 0) {
        return {
          title: "Browse Auction Categories",
          subtitle: "Select an auction category to explore sub-categories and live auction listings.",
          items: auctionBrowseItems,
          breadcrumbTrail: ["Auctions"],
          showStats: true,
          itemLabel: "Categories",
          cardCtaLabel: "View Subcategories",
          currentCrumbLabel: "Auctions",
        };
      }
      return {
        title: activeAuctionCategory?.name || "Auction Sub Categories",
        subtitle: activeAuctionCategory?.name
          ? `Browse ${activeAuctionCategory.name} sub-categories to find live auctions and place bids.`
          : "Choose a sub-category to view auction listings.",
        items: auctionBrowseItems,
        breadcrumbTrail: ["Auctions", activeAuctionCategory?.name].filter(Boolean),
        showStats: true,
        itemLabel: "Subcategories",
        cardCtaLabel: "View Auctions",
        currentCrumbLabel: "Category",
        parentCategory: activeAuctionCategory,
      };
    }
    if (isInfraBrowse) {
      const typeLabel = infraListingTypeLabel(applied.infraType);
      if (infraBrowseDepth === 0) {
        return {
          title: "Browse by Listing Type",
          subtitle: "Select property or machinery to explore asset categories.",
          items: infraBrowseItems,
          breadcrumbTrail: ["Infrastructure Leasing"],
          showStats: true,
          itemLabel: "Listing Types",
          cardCtaLabel: "Browse Categories",
          currentCrumbLabel: "Infrastructure Leasing",
        };
      }
      if (infraBrowseDepth === 1) {
        return {
          title:
            applied.infraType === "machinery"
              ? "Machinery / Asset Categories"
              : "Property Asset Categories",
          subtitle: "Choose an asset category to continue.",
          items: infraBrowseItems,
          breadcrumbTrail: ["Infrastructure Leasing", typeLabel].filter(Boolean),
          showStats: true,
          itemLabel: "Asset Categories",
          cardCtaLabel: "View Subcategories",
          currentCrumbLabel: "Listing Type",
        };
      }
      if (infraBrowseDepth === 2) {
        return {
          title: activeInfraAssetCategory?.name || "Sub Categories",
          subtitle:
            applied.infraType === "property"
              ? "Choose a sub-category to view listings."
              : "Choose a sub-category to explore further.",
          items: infraBrowseItems,
          breadcrumbTrail: ["Infrastructure Leasing", typeLabel, activeInfraAssetCategory?.name].filter(Boolean),
          showStats: true,
          itemLabel: "Subcategories",
          cardCtaLabel: applied.infraType === "property" ? "View Listings" : "View Subcategories",
          currentCrumbLabel: "Asset Category",
          parentCategory: activeInfraAssetCategory,
        };
      }
      return {
        title: activeInfraSubCategory?.name || "Sub-Sub Categories",
        subtitle: "Choose a sub-sub-category to view listings.",
        items: infraBrowseItems,
        breadcrumbTrail: [
          "Infrastructure Leasing",
          typeLabel,
          activeInfraAssetCategory?.name,
          activeInfraSubCategory?.name,
        ].filter(Boolean),
        showStats: true,
        itemLabel: "Subcategories",
        cardCtaLabel: "View Listings",
        currentCrumbLabel: "Subcategory",
        parentCategory: activeInfraSubCategory,
      };
    }
    if (isCmIndustryBrowse) {
      return {
        title: "Contract Manufacturing Industries",
        subtitle:
          "Select an industry to find verified contract manufacturers, production partners, and custom manufacturing options.",
        items: cmIndustryItems,
        breadcrumbTrail: [],
        showStats: true,
      };
    }
    if (browseLevel === 1) {
      return {
        title: "Explore Business Categories",
        subtitle:
          "Browse categories to find products, suppliers, and buyer requirements across the B2B marketplace.",
        items: masterCategories,
        breadcrumbTrail: [],
        showStats: true,
      };
    }
    if (browseLevel === 2) {
      return {
        title: activeCategory?.name || "Sub Categories",
        subtitle:
          activeCategory?.name
            ? `Browse ${activeCategory.name} subcategories to find verified suppliers, buyer requirements, RFQs, and live auctions.`
            : "Choose a sub-category to continue exploring products and suppliers.",
        items: browseSubCategories,
        breadcrumbTrail: [activeCategory?.name].filter(Boolean),
        showStats: true,
      };
    }
    return {
      title: "Explore Business Categories",
      subtitle: "",
      items: [],
      breadcrumbTrail: [],
      showStats: false,
    };
  }, [
    browseLevel,
    isCmIndustryBrowse,
    isInfraBrowse,
    isAuctionBrowse,
    auctionBrowseDepth,
    infraBrowseDepth,
    applied.infraType,
    masterCategories,
    browseSubCategories,
    cmIndustryItems,
    activeCategory,
    activeAuctionCategory,
    infraAssetCategoryItems,
    infraSubCategoryItems,
    infraSubSubCategoryItems,
    activeInfraAssetCategory,
    infraBrowseItems,
    auctionBrowseItems,
    infraPublishedListings,
  ]);

  const infraBrowseLoading =
    infraPublishedListingsLoading ||
    (infraBrowseDepth === 1 && loadingFacilityTypes) ||
    (infraBrowseDepth === 2 && loadingFacilitySubTypes) ||
    (infraBrowseDepth === 3 && loadingFacilitySubSubTypes);

  const auctionModuleBrowseLoading = auctionBrowseLoading || auctionSubCategoriesLoading;

  if (infraBrowseDepth === 4 || auctionBrowseDepth === 2) {
    return null;
  }

  if (cmManufacturerBrowse) {
    return (
      <>
        <div className="product-marketplace-page product-marketplace-page--explore product-marketplace-page--subcategory-products">
          <B2BHubHomeHeader />
          <ExploreCmIndustryManufacturersBrowse
            industry={activeCmIndustry}
            manufacturers={paginatedCmIndustryManufacturers}
            loading={cmIndustryManufacturersLoading}
            applied={applied}
            pagination={cmIndustryManufacturerPagination}
            pageSize={pageSize}
            pageNumbers={cmIndustryManufacturerPageNumbers}
            filters={cmIndustryFilters}
            onFiltersChange={setCmIndustryFilters}
            onClearFilters={clearCmIndustryFilters}
            filterOptions={cmIndustryManufacturerFilterOptions}
            industryFilterOptions={cmIndustryFilterOptions}
            rfqSentCompanyIds={cmRfqSentCompanyIds}
            onSearchSubmit={submitCmManufacturerSearch}
            onSortChange={handleCmManufacturerSortChange}
            onGoToPage={goToPage}
            onChangePageSize={changePageSize}
            onToggleCompare={toggleCmCompare}
            compareIds={cmCompareIds}
            advertisementBanners={marketplaceBanners}
            onBreadcrumbNavigate={navigateCmManufacturerBreadcrumb}
          />
        </div>

        {cmCompareIds.length > 0 ? (
          <div className="marketplace-compare-bar">
            <div className="marketplace-compare-bar__inner">
              <p>
                {cmCompareIds.length} manufacturer{cmCompareIds.length === 1 ? "" : "s"} selected
                {cmCompareIds.length < MAX_COMPARE_PRODUCTS
                  ? ` — add up to ${MAX_COMPARE_PRODUCTS - cmCompareIds.length} more`
                  : " — maximum reached"}
              </p>
              <div className="marketplace-compare-bar__actions">
                <button
                  type="button"
                  className="marketplace-compare-bar__clear"
                  onClick={() => {
                    setCmCompareIds([]);
                    setCmCompareById({});
                    setCmCompareModalOpen(false);
                  }}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="marketplace-compare-bar__view"
                  disabled={cmCompareIds.length < 2}
                  onClick={() => setCmCompareModalOpen(true)}
                >
                  <FaBalanceScale aria-hidden /> View Comparison
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <ContractManufacturerComparisonModal
          open={cmCompareModalOpen}
          manufacturers={compareManufacturers}
          industryFilterOptions={cmIndustryFilterOptions}
          onClose={() => setCmCompareModalOpen(false)}
        />
        <HubFloatingRail />
      </>
    );
  }

  if (subcategoryProductBrowse) {
    return (
      <>
        <div className="product-marketplace-page product-marketplace-page--explore product-marketplace-page--subcategory-products">
          <B2BHubHomeHeader />
          <ExploreSubcategoryProductsBrowse
            parentCategory={activeCategory}
            subCategory={activeSubCategory}
            subSubCategories={browseSubSubCategories}
            products={products}
            loading={loading}
            applied={applied}
            draft={draft}
            setDraft={setDraft}
            categoryOptions={categoryOptions}
            subCategoryOptions={subCategoryOptions}
            subSubCategoryOptions={subSubCategoryOptions}
            brands={brands}
            locations={locations}
            pagination={pagination}
            pageSize={pageSize}
            pageNumbers={pageNumbers}
            onApplyFilters={applyFilters}
            onClearFilters={clearFilters}
            onSearchSubmit={submitSubcategorySearch}
            onSortChange={handleSubcategorySortChange}
            onGoToPage={goToPage}
            onChangePageSize={changePageSize}
            onToggleCompare={toggleCompare}
            compareIds={compareIds}
            subcategoryProductBanners={marketplaceSubcategoryProductBanners}
          />
        </div>

        {compareIds.length > 0 ? (
          <div className="marketplace-compare-bar">
            <div className="marketplace-compare-bar__inner">
              <p>
                {compareIds.length} product{compareIds.length === 1 ? "" : "s"} selected
                {compareIds.length < MAX_COMPARE_PRODUCTS
                  ? ` — add up to ${MAX_COMPARE_PRODUCTS - compareIds.length} more`
                  : " — maximum reached"}
              </p>
              <div className="marketplace-compare-bar__actions">
                <button type="button" className="marketplace-compare-bar__clear" onClick={() => {
                  setCompareIds([]);
                  setCompareProductsById({});
                  setCompareModalOpen(false);
                }}>
                  Clear
                </button>
                <button
                  type="button"
                  className="marketplace-compare-bar__view"
                  disabled={compareIds.length < 2}
                  onClick={() => setCompareModalOpen(true)}
                >
                  <FaBalanceScale aria-hidden /> View Comparison
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <MarketplaceProductComparisonModal
          open={compareModalOpen}
          products={compareProducts}
          onClose={() => setCompareModalOpen(false)}
        />
        <HubFloatingRail />
      </>
    );
  }

  if (browseLevel < 4) {
    return (
      <>
      <div className="product-marketplace-page product-marketplace-page--explore">
        <B2BHubHomeHeader />
        <ExploreProductCategoriesBrowse
          browseLevel={browseLevel}
          title={exploreBrowseCopy.title}
          subtitle={exploreBrowseCopy.subtitle}
          breadcrumbTrail={exploreBrowseCopy.breadcrumbTrail}
          items={exploreBrowseCopy.items}
          loading={
            browseLoading ||
            (isCmIndustryBrowse && cmManufacturersLoading) ||
            infraBrowseLoading ||
            auctionModuleBrowseLoading
          }
          searchValue={draft.q}
          onSearchChange={(value) => setDraft((d) => ({ ...d, q: value }))}
          onSearchSubmit={(query) => submitSearch(query)}
          onSelect={handleBrowseSelect}
          onBack={goBack}
          backLabel={browseLevel === 1 && !applied.module ? "Back to Home" : "Back"}
          showStats={exploreBrowseCopy.showStats}
          parentCategory={exploreBrowseCopy.parentCategory || activeCategory}
          advertisementBanners={marketplaceBanners}
          subcategoryBanners={marketplaceSubcategoryBanners}
          activeModuleKey={
            isCmIndustryBrowse ? "cm" : isInfraBrowse ? "infra" : isAuctionBrowse ? "auctions" : "products"
          }
          onModuleSelect={selectMarketplaceModule}
          itemLabelOverride={exploreBrowseCopy.itemLabel}
          cardCtaLabel={exploreBrowseCopy.cardCtaLabel}
          currentCrumbLabel={
            exploreBrowseCopy.currentCrumbLabel ||
            (isCmIndustryBrowse ? "Contract Manufacturing" : isAuctionBrowse ? "Auctions" : "Categories")
          }
          cardDescriptionBuilder={
            isCmIndustryBrowse
              ? (item) =>
                  `Find verified contract manufacturers for ${String(item.name || "").replace(/\s*\(\d+\)\s*$/, "")}. Compare capabilities, locations, and RFQ-ready production partners.`
              : isInfraBrowse
                ? (item) => item.description || `Explore ${item.name} listings for infrastructure leasing.`
                : isAuctionBrowse
                  ? (item) =>
                      item.description ||
                      (auctionBrowseDepth === 0
                        ? `Browse live auctions in ${item.name}. View sub-categories and place bids on verified listings.`
                        : `Explore live ${item.name} auctions. View listings, compare bids, and participate in verified auctions.`)
                  : undefined
          }
          cardVariant={
            isCmIndustryBrowse ? "cm-industry" : isInfraBrowse ? "infra" : isAuctionBrowse ? "auction" : undefined
          }
          infraBrowseDepth={isInfraBrowse ? infraBrowseDepth : -1}
          auctionBrowseDepth={isAuctionBrowse ? auctionBrowseDepth : -1}
          infraListingType={applied.infraType}
          filtersSidebar={
            isCmIndustryBrowse ? (
              <ContractManufacturersFiltersPanel
                filters={cmFilters}
                onChange={setCmFilters}
                onClear={clearCmFilters}
                stateFilterOptions={cmFilterOptions.stateFilterOptions}
                cityFilterOptions={cmFilterOptions.cityFilterOptions}
                cmTypeFilterOptions={cmFilterOptions.cmTypeFilterOptions}
                facilityTypeFilterOptions={cmFilterOptions.facilityTypeFilterOptions}
                sidebarIndustryOptions={cmFilterOptions.sidebarIndustryOptions}
                sidebarCertFilterOptions={cmFilterOptions.sidebarCertFilterOptions}
                searchLabel="Search Manufacturer"
                searchPlaceholder="Search industries or manufacturers…"
              />
            ) : (
              <ProductMarketplaceFiltersPanel
                draft={draft}
                setDraft={setDraft}
                categoryOptions={categoryOptions}
                subCategoryOptions={subCategoryOptions}
                subSubCategoryOptions={subSubCategoryOptions}
                locations={locations}
                brands={brands}
                onApply={applyFilters}
                onClear={clearFilters}
              />
            )
          }
        />
      </div>
      <HubFloatingRail />
      </>
    );
  }

  return (
    <div className="product-marketplace-page">
      <B2BHubHomeHeader />

      <main className="product-marketplace-main">
        <nav className="product-marketplace-breadcrumb" aria-label="Breadcrumb">
          <Link to="/home">Home</Link>
          <span className="product-marketplace-breadcrumb__sep">&gt;</span>
          {browseLevel === 1 ? (
            <span className="product-marketplace-breadcrumb__current">Marketplace</span>
          ) : (
            <Link to="/marketplace/products">Marketplace</Link>
          )}
          {applied.category ? (
            <>
              <span className="product-marketplace-breadcrumb__sep">&gt;</span>
              {browseLevel > 2 ? (
                <Link to={breadcrumbHref({ category: applied.category })}>
                  {activeCategory?.name || "Category"}
                </Link>
              ) : (
                <span className="product-marketplace-breadcrumb__current">{activeCategory?.name || "Category"}</span>
              )}
            </>
          ) : null}
          {applied.subCategory ? (
            <>
              <span className="product-marketplace-breadcrumb__sep">&gt;</span>
              {browseLevel > 3 ? (
                <Link
                  to={breadcrumbHref({ category: applied.category, subCategory: applied.subCategory })}
                >
                  {activeSubCategory?.name || activeSubCategory?.sub_category || "Sub Category"}
                </Link>
              ) : (
                <span className="product-marketplace-breadcrumb__current">
                  {activeSubCategory?.name || activeSubCategory?.sub_category || "Sub Category"}
                </span>
              )}
            </>
          ) : null}
          {applied.subSubCategory ? (
            <>
              <span className="product-marketplace-breadcrumb__sep">&gt;</span>
              <span className="product-marketplace-breadcrumb__current">
                {activeSubSubCategory?.name || activeSubSubCategory?.sub_sub_category || "Products"}
              </span>
            </>
          ) : null}
          {hasHeaderSearch && !applied.category ? (
            <>
              <span className="product-marketplace-breadcrumb__sep">&gt;</span>
              <span className="product-marketplace-breadcrumb__current">Search: {applied.q.trim()}</span>
            </>
          ) : null}
        </nav>

        <div className="product-marketplace-heading">
          <div className="product-marketplace-heading__text">
            <h1>Product Marketplace</h1>
            <p>
              {hasHeaderSearch
                ? `Showing products matching "${applied.q.trim()}".`
                : browseLevel === 4
                  ? "Products matching your filters."
                  : browseLevel > 1
                    ? "Browse categories step by step, or use search and filters to find products directly."
                    : "Browse categories or search and filter to find products without navigating the full hierarchy."}
            </p>
          </div>
          <div className="product-marketplace-heading__actions">
            {browseLevel > 1 || hasProductDiscoveryFilters(applied) ? (
              <button type="button" className="product-marketplace-back" onClick={goBack}>
                <FaChevronLeft aria-hidden /> Back
              </button>
            ) : null}
            <button
              type="button"
              className="product-marketplace-how"
              onClick={() =>
                toast.info(
                  "Browse categories step by step, or search and apply filters to find products without knowing the exact category path.",
                )
              }
            >
              <FaQuestionCircle aria-hidden /> How it works
            </button>
          </div>
        </div>

        <form
          className="product-marketplace-search-bar"
          onSubmit={(e) => {
            e.preventDefault();
            submitSearch(draft.q);
          }}
        >
          <FaSearch className="product-marketplace-search-bar__icon" aria-hidden />
          <input
            type="search"
            className="product-marketplace-search-bar__input"
            placeholder="Search products by name, brand, category, or seller…"
            value={draft.q}
            onChange={(e) => setDraft((d) => ({ ...d, q: e.target.value }))}
            aria-label="Search products"
          />
          {draft.q ? (
            <button
              type="button"
              className="product-marketplace-search-bar__clear"
              aria-label="Clear search"
              onClick={() => setDraft((d) => ({ ...d, q: "" }))}
            >
              <FaTimes aria-hidden />
            </button>
          ) : null}
          <button type="submit" className="product-marketplace-search-bar__submit">
            Search
          </button>
        </form>

        <div className="product-marketplace-layout">
          <ProductMarketplaceFiltersPanel
            draft={draft}
            setDraft={setDraft}
            categoryOptions={categoryOptions}
            subCategoryOptions={subCategoryOptions}
            subSubCategoryOptions={subSubCategoryOptions}
            locations={locations}
            brands={brands}
            onApply={applyFilters}
            onClear={clearFilters}
          />

          <section className="product-marketplace-content">
            <>
            <div className="product-marketplace-panel product-marketplace-panel--tabs">
              <p className="product-marketplace-tabs-section__title">Categories</p>
              <CategoryTabsRow
                ariaLabel="Product categories"
                items={categoryTabItems}
                activeId={applied.category}
                onSelect={selectCategory}
                allLabel="All Categories"
              />

              {applied.category && subCategoryTabItems.length > 0 ? (
                <div className="product-marketplace-tabs-section">
                  <p className="product-marketplace-tabs-section__title">
                    Sub Categories{activeCategory?.name ? ` (${activeCategory.name})` : ""}
                  </p>
                  <CategoryTabsRow
                    ariaLabel="Sub categories"
                    items={subCategoryTabItems}
                    activeId={applied.subCategory}
                    onSelect={selectSubCategory}
                    allLabel="All Sub Categories"
                  />
                </div>
              ) : null}

              {applied.subCategory && subSubCategoryTabItems.length > 0 ? (
                <div className="product-marketplace-tabs-section">
                  <p className="product-marketplace-tabs-section__title">
                    Sub Sub Categories
                    {activeSubCategory?.sub_category || activeSubCategory?.name
                      ? ` (${activeSubCategory.sub_category || activeSubCategory.name})`
                      : ""}
                  </p>
                  <CategoryTabsRow
                    ariaLabel="Sub-sub categories"
                    items={subSubCategoryTabItems}
                    activeId={applied.subSubCategory}
                    onSelect={selectSubSubCategory}
                    allLabel="All Sub Sub Categories"
                  />
                </div>
              ) : null}
            </div>

            <div className="product-marketplace-panel product-marketplace-panel--products">
              <div className="product-marketplace-toolbar">
                {resultBreadcrumbParts.length > 0 ? (
                  <div className="product-marketplace-toolbar__breadcrumb">
                    <span className="product-marketplace-toolbar__breadcrumb-label">Showing results for:</span>
                    <span className="product-marketplace-toolbar__breadcrumb-path">
                      {resultBreadcrumbParts.join(" > ")}
                    </span>
                    <button type="button" className="product-marketplace-toolbar__breadcrumb-clear" onClick={clearCategoryPath}>
                      <FaTimes aria-hidden /> Clear
                    </button>
                  </div>
                ) : hasHeaderSearch ? (
                  <div className="product-marketplace-toolbar__breadcrumb">
                    <span className="product-marketplace-toolbar__breadcrumb-label">Showing results for:</span>
                    <span className="product-marketplace-toolbar__breadcrumb-path">"{applied.q.trim()}"</span>
                    <button
                      type="button"
                      className="product-marketplace-toolbar__breadcrumb-clear"
                      onClick={() => patchFilters({ q: "", discover: "" })}
                    >
                      <FaTimes aria-hidden /> Clear
                    </button>
                  </div>
                ) : (
                  <div className="product-marketplace-toolbar__breadcrumb product-marketplace-toolbar__breadcrumb--empty" />
                )}

                <div className="product-marketplace-toolbar__controls">
                  <div className="product-marketplace-toolbar__view-toggle" aria-label="View mode">
                    <button
                      type="button"
                      className={viewMode === "grid" ? "is-active" : ""}
                      onClick={() => setViewMode("grid")}
                    >
                      <FaTh aria-hidden /> Grid
                    </button>
                    <button
                      type="button"
                      className={viewMode === "list" ? "is-active" : ""}
                      onClick={() => setViewMode("list")}
                    >
                      <FaList aria-hidden /> List
                    </button>
                  </div>
                  <select
                    className="product-marketplace-toolbar__sort"
                    value={draft.sort}
                    onChange={(e) => {
                      const sort = e.target.value;
                      setDraft((d) => ({ ...d, sort }));
                      setSearchParams(buildSearchParams(searchParams, { sort, page: "" }));
                    }}
                    aria-label="Sort products"
                  >
                    {SORT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        Sort By: {opt.label}
                      </option>
                    ))}
                  </select>
                  <button type="button" className="product-marketplace-btn product-marketplace-btn--compare">
                    <FaBalanceScale aria-hidden /> Compare ({compareIds.length})
                  </button>
                </div>
              </div>

              <p className="product-marketplace-count">
                {showingTotal > 0 ? `${showingTotal.toLocaleString()} Products Found` : "0 Products Found"}
              </p>

              <div className={`product-marketplace-grid product-marketplace-grid--${viewMode}`}>
                {loading && products.length === 0
                  ? Array.from({ length: pageSize }).map((_, idx) => (
                      <div key={idx} className="product-marketplace-skeleton" />
                    ))
                  : null}
                {!loading || products.length
                  ? products.map((item) => (
                      <MarketplaceProductCard
                        key={item.id}
                        item={item}
                        onToggleWish={toggleWish}
                        wished={wishIds.includes(item.id)}
                      />
                    ))
                  : null}
                {!loading && products.length === 0 ? (
                  <p className="product-marketplace-empty">No products match your filters yet.</p>
                ) : null}
              </div>
            </div>

            {pagination.total_pages > 0 ? (
              <div className="product-marketplace-pagination-wrap">
                <nav className="product-marketplace-pagination" aria-label="Pagination">
                  <button type="button" disabled={Number(applied.page) <= 1} onClick={() => goToPage(Number(applied.page) - 1)}>
                    <FaChevronLeft aria-hidden /> Previous
                  </button>
                  {pageNumbers.map((page, idx) =>
                    page === "…" ? (
                      <span key={`ellipsis-${idx}`} className="product-marketplace-pagination__ellipsis">
                        …
                      </span>
                    ) : (
                      <button
                        key={page}
                        type="button"
                        className={Number(applied.page) === page ? "is-active" : ""}
                        onClick={() => goToPage(page)}
                      >
                        {page}
                      </button>
                    ),
                  )}
                  <button
                    type="button"
                    disabled={Number(applied.page) >= pagination.total_pages}
                    onClick={() => goToPage(Number(applied.page) + 1)}
                  >
                    Next <FaChevronRight aria-hidden />
                  </button>
                </nav>
                <label className="product-marketplace-page-size">
                  Show
                  <select
                    value={pageSize}
                    onChange={(e) => changePageSize(Number(e.target.value))}
                    aria-label="Products per page"
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                  per page
                </label>
              </div>
            ) : null}
            </>
          </section>
        </div>
      </main>
      <HubFloatingRail />
    </div>
  );
}
